import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
} from "@mui/material";
import { Send as SendIcon, Delete as DeleteIcon, Refresh, Schedule, Image, VideoLibrary, Link } from "@mui/icons-material";
import { api_urls } from "../../utils/api-urls/index.js";
import MainDatatable from "../../components/common/MainDatatable.jsx";

const DEEP_LINK_OPTIONS = [
  { value: "", label: "None (No redirect)" },
  { value: "flight", label: "✈️ Flight Booking" },
  { value: "hotel", label: "🏨 Hotel Booking" },
  { value: "fastag", label: "🚗 FASTag Recharge" },
  { value: "milansathi", label: "💕 Milan Sathi" },
  { value: "prepaid", label: "📱 Mobile Prepaid" },
  { value: "postpaid", label: "📞 Mobile Postpaid" },
  { value: "dth", label: "📺 DTH Recharge" },
  { value: "gas", label: "🔥 Gas Booking" },
  { value: "electricity", label: "⚡ Electricity Bill" },
  { value: "yatra", label: "🚌 Yatra Tours" },
  { value: "live", label: "📺 Live Streaming" },
  { value: "kundli", label: "🔮 Kundli" },
  { value: "pooja", label: "🙏 Pooja Booking" },
  { value: "podcast", label: "🎧 Podcast" },
  { value: "video", label: "🎬 Videos" },
  { value: "news", label: "📰 News" },
  { value: "shopping", label: "🛒 Shopping" },
  { value: "wallet", label: "💰 Wallet" },
  { value: "home", label: "🏠 Home" },
];

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
  // Form states
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [type, setType] = useState("all");
  const [image, setImage] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [deepLink, setDeepLink] = useState("");
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [targetUsers, setTargetUsers] = useState([]);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [sending, setSending] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(api_urls + "api/admin/notifications");
      if (response.data && response.data.success) {
        setNotifications(response.data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(api_urls + "api/admin/notifications/stats");
      if (response.data && response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const searchUsers = async (search) => {
    if (!search || search.length < 2) {
      setUserSearchResults([]);
      return;
    }
    try {
      setUserSearchLoading(true);
      const response = await axios.get(api_urls + "api/admin/notifications/target-users?search=" + encodeURIComponent(search) + "&limit=50");
      if (response.data && response.data.success) {
        setUserSearchResults(response.data.data || []);
      }
    } catch (error) {
      console.error("Failed to search users:", error);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleSendNotification = async () => {
    if (!title || !body) {
      setSnackbar({ open: true, message: "Title and body are required", severity: "error" });
      return;
    }
    if (type === "specific" && targetUsers.length === 0) {
      setSnackbar({ open: true, message: "Please select at least one user", severity: "error" });
      return;
    }
    if (isScheduled && !scheduledAt) {
      setSnackbar({ open: true, message: "Please select schedule date and time", severity: "error" });
      return;
    }
    try {
      setSending(true);
      const adminId = localStorage.getItem("userId");
      const payload = {
        title,
        body,
        type,
        adminId,
        image: image || null,
        videoUrl: videoUrl || null,
        deepLink: deepLink || null,
        targetUsers: type === "specific" ? targetUsers.map(u => u._id) : [],
        scheduledAt: isScheduled ? new Date(scheduledAt).toISOString() : null,
      };
      const response = await axios.post(api_urls + "api/admin/notifications/send", payload);
      if (response.data && response.data.success) {
        setSnackbar({ open: true, message: response.data.message, severity: "success" });
        setSendDialogOpen(false);
        resetForm();
        fetchNotifications();
        fetchStats();
      } else {
        setSnackbar({ open: true, message: response.data.message || "Failed to send", severity: "error" });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to send notification", severity: "error" });
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setTitle("");
    setBody("");
    setType("all");
    setImage("");
    setVideoUrl("");
    setDeepLink("");
    setIsScheduled(false);
    setScheduledAt("");
    setTargetUsers([]);
    setUserSearch("");
    setUserSearchResults([]);
  };

  const handleDelete = async (notificationId) => {
    if (!window.confirm("Are you sure you want to delete this notification?")) return;
    try {
      await axios.post(api_urls + "api/admin/notifications/delete", { notificationId });
      setSnackbar({ open: true, message: "Notification deleted", severity: "success" });
      fetchNotifications();
      fetchStats();
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to delete notification", severity: "error" });
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchStats();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchUsers(userSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearch]);

  const getStatusColor = (status) => {
    switch (status) {
      case "sent": return "success";
      case "partial": return "warning";
      case "failed": return "error";
      case "scheduled": return "info";
      default: return "default";
    }
  };

  // Helper to get display name for user
  const getUserDisplayName = (option) => {
    const name = option.name || option.firstName || option.lastName || "User";
    const contact = option.phone || option.email || "No contact";
    const fcmStatus = option.hasFCM ? "✅" : "❌";
    return `${fcmStatus} ${name} - ${contact}`;
  };

  // Helper to get chip label
  const getChipLabel = (option) => {
    return option.phone || option.email || "User";
  };

  const columns = [
    { name: "S.No.", selector: (row, index) => index + 1, width: "60px" },
    { name: "Title", selector: (row) => row.title, width: "180px" },
    { name: "Body", selector: (row) => (row.body || "").substring(0, 40) + "...", width: "200px" },
    { name: "Type", selector: (row) => row.type, width: "80px" },
    { 
      name: "Status", 
      cell: (row) => <Chip label={row.status} size="small" color={getStatusColor(row.status)} />,
      width: "100px" 
    },
    { name: "Deep Link", selector: (row) => row.deepLink || "-", width: "100px" },
    { name: "Sent", selector: (row) => row.sentCount || 0, width: "70px" },
    { name: "Failed", selector: (row) => row.failedCount || 0, width: "70px" },
    { 
      name: "Scheduled", 
      selector: (row) => row.scheduledAt ? moment(row.scheduledAt).format("DD MMM HH:mm") : "-",
      width: "120px" 
    },
    { name: "Created", selector: (row) => moment(row.createdAt).format("DD MMM HH:mm"), width: "120px" },
    {
      name: "Actions",
      cell: (row) => (
        <IconButton color="error" onClick={() => handleDelete(row._id)} size="small">
          <DeleteIcon />
        </IconButton>
      ),
      width: "80px",
    },
  ];

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Push Notifications</Typography>
        <Box>
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => { fetchNotifications(); fetchStats(); }} sx={{ mr: 2 }}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<SendIcon />} onClick={() => setSendDialogOpen(true)}>
            Send New Notification
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} md={2}>
            <Card><CardContent>
              <Typography variant="h4" color="primary">{stats.total}</Typography>
              <Typography color="textSecondary" variant="body2">Total</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card><CardContent>
              <Typography variant="h4" color="success.main">{stats.sent}</Typography>
              <Typography color="textSecondary" variant="body2">Sent</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card><CardContent>
              <Typography variant="h4" color="warning.main">{stats.scheduled}</Typography>
              <Typography color="textSecondary" variant="body2">Scheduled</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card><CardContent>
              <Typography variant="h4" color="error.main">{stats.failed}</Typography>
              <Typography color="textSecondary" variant="body2">Failed</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card><CardContent>
              <Typography variant="h4" color="info.main">{stats.totalDevicesReached}</Typography>
              <Typography color="textSecondary" variant="body2">Devices Reached</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} md={2}>
            <Card><CardContent>
              <Typography variant="h4" color="secondary.main">{stats.totalUsersWithFCM}/{stats.totalUsers || 0}</Typography>
              <Typography color="textSecondary" variant="body2">FCM / Total Users</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      {/* Notifications Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : (
        <MainDatatable data={notifications} columns={columns} title="Notification History" />
      )}

      {/* Send Notification Dialog */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Send Push Notification</DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
            <Tab label="Basic" />
            <Tab label="Media & Links" />
            <Tab label="Target & Schedule" />
          </Tabs>

          {tabValue === 0 && (
            <Box>
              <TextField
                fullWidth label="Notification Title *" value={title}
                onChange={(e) => setTitle(e.target.value)} sx={{ mt: 1, mb: 2 }}
                placeholder="Enter notification title"
              />
              <TextField
                fullWidth label="Notification Body *" value={body}
                onChange={(e) => setBody(e.target.value)} multiline rows={4} sx={{ mb: 2 }}
                placeholder="Enter notification message"
              />
            </Box>
          )}

          {tabValue === 1 && (
            <Box>
              <TextField
                fullWidth label="Image URL (optional)" value={image}
                onChange={(e) => setImage(e.target.value)} sx={{ mt: 1, mb: 2 }}
                placeholder="https://example.com/image.jpg"
                InputProps={{ startAdornment: <Image sx={{ mr: 1, color: "action.active" }} /> }}
              />
              <TextField
                fullWidth label="Video URL (optional)" value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)} sx={{ mb: 2 }}
                placeholder="https://youtube.com/watch?v=..."
                InputProps={{ startAdornment: <VideoLibrary sx={{ mr: 1, color: "action.active" }} /> }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Deep Link (Redirect To)</InputLabel>
                <Select value={deepLink} onChange={(e) => setDeepLink(e.target.value)} label="Deep Link (Redirect To)">
                  {DEEP_LINK_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {tabValue === 2 && (
            <Box>
              <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
                <InputLabel>Send To</InputLabel>
                <Select value={type} onChange={(e) => setType(e.target.value)} label="Send To">
                  <MenuItem value="all">All Users ({stats?.totalUsersWithFCM || 0} devices with FCM)</MenuItem>
                  <MenuItem value="specific">Specific Users</MenuItem>
                </Select>
              </FormControl>

              {type === "specific" && (
                <Autocomplete
                  multiple
                  options={userSearchResults}
                  value={targetUsers}
                  onChange={(e, newValue) => setTargetUsers(newValue)}
                  getOptionLabel={getUserDisplayName}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  inputValue={userSearch}
                  onInputChange={(e, value) => setUserSearch(value)}
                  loading={userSearchLoading}
                  noOptionsText={userSearch.length < 2 ? "Type at least 2 characters to search..." : "No users found"}
                  loadingText="Searching users..."
                  filterOptions={(x) => x}
                  renderOption={(props, option) => (
                    <li {...props} key={option._id}>
                      <Box>
                        <Typography variant="body1">
                          {option.hasFCM ? "✅" : "❌"} {option.name || option.firstName || "User"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {option.phone || "No phone"} | {option.email || "No email"}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Search Users" 
                      placeholder="Type phone number, email or name..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {userSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip 
                        {...getTagProps({ index })} 
                        key={option._id}
                        label={`${option.hasFCM ? "✅" : "❌"} ${getChipLabel(option)}`} 
                        size="small" 
                        color={option.hasFCM ? "success" : "default"}
                      />
                    ))
                  }
                  sx={{ mb: 2 }}
                />
              )}

              {type === "specific" && targetUsers.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Selected {targetUsers.length} user(s). 
                  {targetUsers.filter(u => u.hasFCM).length} have FCM tokens (will receive notification).
                  {targetUsers.filter(u => !u.hasFCM).length > 0 && (
                    <strong> {targetUsers.filter(u => !u.hasFCM).length} without FCM will NOT receive.</strong>
                  )}
                </Alert>
              )}

              <FormControlLabel
                control={<Switch checked={isScheduled} onChange={(e) => setIsScheduled(e.target.checked)} />}
                label="Schedule for later"
              />

              {isScheduled && (
                <TextField
                  fullWidth type="datetime-local" value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)} sx={{ mt: 2 }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: new Date().toISOString().slice(0, 16) }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSendDialogOpen(false); resetForm(); }}>Cancel</Button>
          <Button
            variant="contained" onClick={handleSendNotification} disabled={sending}
            startIcon={sending ? <CircularProgress size={20} /> : (isScheduled ? <Schedule /> : <SendIcon />)}
          >
            {sending ? "Processing..." : (isScheduled ? "Schedule Notification" : "Send Now")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationsPage;
